/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CluEntityCapabilityCollapsableCard } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityCapabilityCollapsableCard';
import { CluEntityListCapabilityTable } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityListCapabilityTable';
import { CluEntityPrebuiltCapabilityTable } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityPrebuiltCapabilityTable';
import { CluEntityRegexCapabilityTable } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityRegexCapabilityTable';
import { cluTeachingBubbleMessages } from 'src/clu/cluTeachingBubbleMessages';
import { CluPrebuiltEntity } from 'src/clu/core/types/CluPrebuiltEntity';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import {
    EntityCapability,
    EntityStore,
    PrebuiltEntityCapability,
    RegexEntityCapability,
    SublistEntityCapability
} from 'src/core/types/EntityStore';
import { EntityComponents } from 'src/core/types/EntitySwagger';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import styled from 'styled-components';

export type CluEntityDetailsPivotProps = {
    overlapButtonId: string;
    entityStore: EntityStore;
};

const messages = defineMessages({
    edit: { id: 'clu.cluEntityDetailsPivot.edit', defaultMessage: 'Edit' },
    list: { id: 'clu.cluEntityDetailsPivot.list', defaultMessage: 'List' },
    delete: { id: 'clu.cluEntityDetailsPivot.delete', defaultMessage: 'Delete' },
    learned: { id: 'clu.cluEntityDetailsPivot.learned', defaultMessage: 'Learned' },
    prebuilt: { id: 'clu.cluEntityDetailsPivot.prebuilt', defaultMessage: 'Prebuilt' },
    regex: { id: 'clu.cluEntityDetailsPivot.regex', defaultMessage: 'Regular expression' },
    searchPlaceholder: { id: 'clu.cluEntityDetailsPivot.searchPlaceholder', defaultMessage: 'Search' },
    taggingUtterances: { id: 'clu.cluEntityDetailsPivot.taggingUtterances', defaultMessage: 'labeling utterances.' },
    deletionModalTitle: { id: 'clu.cluEntityDetailsPivot.deletionModalTitle', defaultMessage: 'Delete the selected capabilities' },
    learnedDescription1: {
        id: 'clu.cluEntityDetailsPivot.learnedDescription1',
        defaultMessage:
            'The Learned component uses the entity labels you label your utterances with to train a machine learned model. The model learns to predict where the entity is based on the context within the utterance. This component is only defined if you add labels by'
    },
    learnedDescription2: {
        id: 'clu.cluEntityDetailsPivot.learnedDescription2',
        defaultMessage: 'If you do not label any utterances with this entity, it will not have a Learned component. '
    },
    deletionModalBody: {
        id: 'clu.cluEntityDetailsPivot.deletionModalBody',
        defaultMessage: 'Are you sure you want to delete the selected capabilities?'
    },
    prebuiltDescription: {
        id: 'clu.cluEntityDetailsPivot.prebuiltDescription',
        defaultMessage: 'A prebuilt component gets your entity to extract common types such as numbers, dates, times, and others.'
    },
    regexDescription: {
        id: 'clu.cluEntityDetailsPivot.regexDescription',
        defaultMessage: 'A regex component matches regular expressions for common patterns. You can associate a key to each expression.'
    },
    listDescription: {
        id: 'clu.cluEntityDetailsPivot.listDescription',
        defaultMessage: 'A list component adds additional phrases to match against, and also returns the list key of the match.'
    },
    required: { id: 'clu.cluEntityDetailsPivot.required', defaultMessage: 'Required' },
    notRequired: { id: 'clu.cluEntityDetailsPivot.notRequired', defaultMessage: 'Not Required' },
    requiredComponents: {
        id: 'clu.cluEntityDetailsPivot.requiredComponents',
        defaultMessage: 'Required component'
    },
    requiredComponentTooltip: {
        id: 'clu.cluEntityDetailsPivot.requiredComponentTooltip',
        defaultMessage: 'An entity will only be returned if the required component is predicted or matched.'
    },
    learnMore: {
        id: 'clu.cluEntityDetailsPivot.learnMore',
        defaultMessage: 'Learn more'
    }
});

const searchButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    rootHovered: {
        backgroundColor: 'transparent'
    },
    root: { height: '100%', fontSize: '14px !important' }
});

const StyledStack = styled(Stack)({
    direction: 'ltr',
    width: '150px'
});

/**
 * A component displaying and managing entity capabilities.
 */
export const CluEntityDetailsPivot = observer((props: CluEntityDetailsPivotProps) => {
    const { entityStore, overlapButtonId } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher, uiStore } = useGlobalState();
    const history = useHistory<{ capability: EntityCapability }>();
    const { selectedItems: listSelectedItems, selection: listSelection } = useSelection<SublistEntityCapability>();
    const { selectedItems: prebuiltSelectedItems, selection: prebuiltSelection } = useSelection<PrebuiltEntityCapability>();
    const { selectedItems: regexSelectedItems, selection: regexSelection } = useSelection<RegexEntityCapability>();

    const [entityCapabilityBeingEdited, setEntityCapabilityBeingEdited] = useState<
        SublistEntityCapability | PrebuiltEntityCapability | RegexEntityCapability
    >();
    const [isDeletionModalHidden, setIsDeletionModalHidden] = useState<boolean>(true);
    const [allPrebuiltsList] = useState<CluPrebuiltEntity[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const openCapabilityCard: EntityCapability = history.location.state?.capability;
    const routes = routeTable[rootStore.currentCluProjectStore.projectType === 'Conversation' ? 'clu' : 'orchestration'];

    const isEditButtonDisabled: boolean =
        listSelectedItems.length + prebuiltSelectedItems.length + regexSelectedItems.length !== 1 || Boolean(entityCapabilityBeingEdited);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const [addedComponentData, setAddedComponentData] = React.useState<{ type: string; name: string }>(null);

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('CluOverlapButton');
        },
        []
    );

    React.useEffect(() => {
        if (
            addedComponentData &&
            (entityStore.prebuilts?.length === 1 || entityStore.sublists?.length === 1) &&
            !uiStore.teachingBubbles.CluOverlapButton
        ) {
            createTeachingBubble(
                'CluOverlapButton',
                localize(cluTeachingBubbleMessages.overlapButtonBody, { name: addedComponentData.name, type: addedComponentData.type }),
                localize(cluTeachingBubbleMessages.overlapButtonTitle),
                `#${overlapButtonId}`,
                true,
                true
            );
        }
    }, [addedComponentData, entityStore.prebuilts, entityStore.sublists, Object.values(uiStore.teachingBubbles)]);

    const commandBarItems = [
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="editEntityButtonLanguageToolTip"
                    hidden={canWrite || item.disabled || isEditButtonDisabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            text={localize(messages.edit)}
                            name={localize(messages.edit)}
                            iconProps={{ iconName: 'Edit' }}
                            data-automation-id="editEntityButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || item.disabled || isEditButtonDisabled}
                            onClick={() =>
                                setEntityCapabilityBeingEdited(listSelectedItems[0] || prebuiltSelectedItems[0] || regexSelectedItems[0])
                            }
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteEntityButtonLanguageToolTip"
                    hidden={
                        canWrite ||
                        item.disabled ||
                        (listSelectedItems.length === 0 && prebuiltSelectedItems.length === 0 && regexSelectedItems.length === 0)
                    }
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            text={localize(messages.delete)}
                            name={localize(messages.delete)}
                            iconProps={{ iconName: 'Delete' }}
                            data-automation-id="deleteEntityButton"
                            onClick={() => setIsDeletionModalHidden(false)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={
                                !canWrite ||
                                item.disabled ||
                                (listSelectedItems.length === 0 && prebuiltSelectedItems.length === 0 && regexSelectedItems.length === 0)
                            }
                        />
                    }
                />
            )
        }
    ];

    const commandBarFarItems: ICommandBarItemProps[] = [
        {
            key: 'requiredComponent',
            onRender: () => (
                <Stack horizontal verticalAlign="center" styles={{ root: { marginRight: '25px' } }}>
                    <LuisText>{localize(messages.requiredComponents)}</LuisText>
                    <CalloutButton
                        isDarkMode={false}
                        ariaLabel={localize(messages.requiredComponents)}
                        content={
                            <LuisText>
                                {localize(messages.requiredComponentTooltip)}
                                &nbsp;
                                <ExternalLink href="https://aka.ms/clu-required">{localize(messages.learnMore)}</ExternalLink>
                            </LuisText>
                        }
                    />
                </Stack>
            )
        }
    ];

    const requiredFetchingTracker = useProgressTracker({
        onComplete: () => {
            setEntityCapabilityBeingEdited(null);
        }
    });

    const onChangeRequired = (toggleValue: boolean, category: EntityComponents) => {
        dispatcher.dispatch('updateCluEntityRequired', {
            name: entityStore.name,
            isRequired: toggleValue,
            category,
            tracker: requiredFetchingTracker,
            toastData: {}
        });
    };

    const isRequired = (category: EntityComponents): boolean => entityStore.requiredComponents.indexOf(category) !== -1;

    const isPrebuilt = (
        capability: SublistEntityCapability | PrebuiltEntityCapability | RegexEntityCapability
    ): capability is PrebuiltEntityCapability => (capability as PrebuiltEntityCapability)?.category !== undefined;

    const isRegex = (
        capability: SublistEntityCapability | PrebuiltEntityCapability | RegexEntityCapability
    ): capability is RegexEntityCapability => (capability as RegexEntityCapability)?.expression !== undefined;

    const sublistFetchingTracker = useProgressTracker({
        onComplete: () => {
            listSelection.setAllSelected(false);
            setEntityCapabilityBeingEdited(null);
        }
    });

    const prebuiltsFetchingTracker = useProgressTracker({
        onComplete: () => {
            prebuiltSelection.setAllSelected(false);
            setEntityCapabilityBeingEdited(null);
        }
    });

    const regexFetchingTracker = useProgressTracker({
        onComplete: () => {
            regexSelection.setAllSelected(false);
            setEntityCapabilityBeingEdited(null);
        }
    });

    const defaultOperationsTracker = useProgressTracker({
        onComplete: () => {
            setIsDeletionModalHidden(true);
            listSelection.setAllSelected(false);
            prebuiltSelection.setAllSelected(false);
            regexSelection.setAllSelected(false);
            setEntityCapabilityBeingEdited(null);
        }
    });

    const handleAddSublistCapability = (sublist: SublistEntityCapability) => {
        setAddedComponentData({ type: 'List', name: entityStore.name });

        dispatcher.dispatch('addCluEntitySublistCapability', {
            name: entityStore.name,
            sublist,
            tracker: sublistFetchingTracker,
            toastData: {}
        });
    };

    const handleUpdateSublistCapability = (sublist: SublistEntityCapability) =>
        dispatcher.dispatch('updateCluEntitySublistCapability', {
            name: entityStore.name,
            sublist,
            tracker: sublistFetchingTracker,
            toastData: {}
        });

    const handleAddPrebuiltCapability = (prebuilt: PrebuiltEntityCapability) => {
        setAddedComponentData({ type: 'Prebuilt', name: entityStore.name });

        dispatcher.dispatch('addCluEntityPrebuiltCapability', {
            name: entityStore.name,
            prebuilt,
            tracker: prebuiltsFetchingTracker,
            toastData: {}
        });
    };

    const handleUpdatePrebuiltCapability = (prebuilt: PrebuiltEntityCapability) =>
        dispatcher.dispatch('updateCluEntityPrebuiltCapability', {
            name: entityStore.name,
            prebuilt,
            tracker: prebuiltsFetchingTracker,
            toastData: {}
        });

    const handleDeleteCapabilities = (capabilities: string[]) =>
        dispatcher.dispatch('deleteCluEntityCapabilities', {
            name: entityStore.name,
            ids: capabilities,
            tracker: defaultOperationsTracker,
            toastData: {}
        });

    const handleAddRegexCapability = (regex: RegexEntityCapability) => {
        setAddedComponentData({ type: 'Regex', name: entityStore.name });
        dispatcher.dispatch('addCluEntityRegexCapability', {
            name: entityStore.name,
            regex,
            tracker: regexFetchingTracker,
            toastData: {}
        });
    };

    const handleUpdateRegexCapability = (regex: RegexEntityCapability) => {
        dispatcher.dispatch('updateCluEntityRegexCapability', {
            name: entityStore.name,
            regex,
            tracker: regexFetchingTracker,
            toastData: {}
        });
    };

    React.useEffect(() => {
        if (allPrebuiltsList.length === 0) {
            dispatcher.dispatch('getCluAvailablePrebuilts', { allPrebuiltsList, tracker: prebuiltsFetchingTracker });
        }
    }, [allPrebuiltsList]);

    const onDeleteCapability = () => {
        handleDeleteCapabilities(
            listSelectedItems
                .map(item => item.id)
                .concat(prebuiltSelectedItems.map(item => item.id))
                .concat(regexSelectedItems.map(item => item.id))
        );
    };

    const listCapabilityFarComponent = (
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center" gap="5">
            <LuisDebouncedCollapsableSearchBox
                buttonStyles={searchButtonStyles}
                styles={{ icon: { fontSize: 14 } }}
                value={searchQuery}
                disableAnimation
                onChange={(_, value) => setSearchQuery(value)}
                ariaLabel={localize(messages.searchPlaceholder)}
                placeholder={localize(messages.searchPlaceholder)}
            />

            <StyledStack>
                <Toggle
                    styles={{ root: { marginBottom: '0px' } }}
                    disabled={requiredFetchingTracker.isRunning || entityStore.sublists.length === 0}
                    onText={localize(messages.required)}
                    offText={localize(messages.notRequired)}
                    onChange={(_, checked: boolean) => onChangeRequired(checked, 'list')}
                    checked={isRequired('list')}
                />
            </StyledStack>
        </Stack>
    );

    return (
        <Stack role="menu">
            <LuisCommandBar items={commandBarItems} farItems={commandBarFarItems} />

            <CluEntityCapabilityCollapsableCard
                title={localize(messages.learned)}
                isCapabilityOpen={openCapabilityCard === 'learned' || !openCapabilityCard}
                farComponent={
                    <StyledStack>
                        <Toggle
                            styles={{ root: { marginBottom: '0px' } }}
                            disabled={requiredFetchingTracker.isRunning}
                            onText={localize(messages.required)}
                            offText={localize(messages.notRequired)}
                            onChange={(_, checked: boolean) => onChangeRequired(checked, 'learned')}
                            checked={isRequired('learned')}
                        />
                    </StyledStack>
                }>
                <LuisText>
                    {localize(messages.learnedDescription1)}
                    &nbsp;
                    <Link onClick={() => history.push(routes.dataTaggingPage(rootStore.currentCluProjectStore.id))}>
                        {localize(messages.taggingUtterances)}
                    </Link>
                    &nbsp;
                    {localize(messages.learnedDescription2)}
                </LuisText>
            </CluEntityCapabilityCollapsableCard>

            <CluEntityCapabilityCollapsableCard
                title={localize(messages.prebuilt)}
                onRenderCount={() => `${entityStore.prebuilts.length}`}
                isCapabilityOpen={openCapabilityCard === 'prebuilt' || !openCapabilityCard}
                farComponent={
                    <StyledStack>
                        <Toggle
                            styles={{ root: { marginBottom: '0px' } }}
                            disabled={requiredFetchingTracker.isRunning || entityStore.prebuilts.length === 0}
                            onText={localize(messages.required)}
                            offText={localize(messages.notRequired)}
                            onChange={(_, checked: boolean) => onChangeRequired(checked, 'prebuilts')}
                            checked={isRequired('prebuilts')}
                        />
                    </StyledStack>
                }>
                <>
                    <LuisText>{localize(messages.prebuiltDescription)}</LuisText>

                    <CluEntityPrebuiltCapabilityTable
                        selection={prebuiltSelection}
                        items={entityStore.prebuilts}
                        allPrebuilts={allPrebuiltsList}
                        arePrebuiltsLoading={prebuiltsFetchingTracker.isRunning}
                        prebuiltBeingEdited={isPrebuilt(entityCapabilityBeingEdited) ? entityCapabilityBeingEdited : null}
                        dismissEditingPrebuilt={() => setEntityCapabilityBeingEdited(null)}
                        onAddPrebuilt={handleAddPrebuiltCapability}
                        onEditPrebuilt={handleUpdatePrebuiltCapability}
                    />
                </>
            </CluEntityCapabilityCollapsableCard>

            {FeatureToggles.CluRegexEntityComponent && (
                <CluEntityCapabilityCollapsableCard
                    title={localize(messages.regex)}
                    onRenderCount={() => `${entityStore.regexList.length}`}
                    isCapabilityOpen={openCapabilityCard === 'regex' || !openCapabilityCard}
                    farComponent={
                        <StyledStack>
                            <Toggle
                                checked={isRequired('regex')}
                                onText={localize(messages.required)}
                                offText={localize(messages.notRequired)}
                                styles={{ root: { marginBottom: '0px' } }}
                                onChange={(_, checked: boolean) => onChangeRequired(checked, 'regex')}
                                disabled={requiredFetchingTracker.isRunning || entityStore.regexList.length === 0}
                            />
                        </StyledStack>
                    }>
                    <>
                        <LuisText>{localize(messages.regexDescription)}</LuisText>

                        <CluEntityRegexCapabilityTable
                            selection={regexSelection}
                            items={entityStore.regexList}
                            onAddRegex={handleAddRegexCapability}
                            onEditRegex={handleUpdateRegexCapability}
                            isLoading={regexFetchingTracker.isRunning}
                            dismissEditingRegex={() => setEntityCapabilityBeingEdited(null)}
                            regexBeingEdited={!isRegex(entityCapabilityBeingEdited) ? null : entityCapabilityBeingEdited}
                        />
                    </>
                </CluEntityCapabilityCollapsableCard>
            )}

            <CluEntityCapabilityCollapsableCard
                title={localize(messages.list)}
                onRenderCount={() => `${entityStore.sublists.length}`}
                isCapabilityOpen={openCapabilityCard === 'list' || !openCapabilityCard}
                farComponent={listCapabilityFarComponent}>
                <Stack>
                    <LuisText>{localize(messages.listDescription)}</LuisText>

                    <CluEntityListCapabilityTable
                        selection={listSelection}
                        onAddSublist={handleAddSublistCapability}
                        isLoading={sublistFetchingTracker.isRunning}
                        onEditSublist={handleUpdateSublistCapability}
                        dismissEditingSublist={() => setEntityCapabilityBeingEdited(null)}
                        onDeleteLastSynonym={() => handleDeleteCapabilities(listSelectedItems.map(item => item.id))}
                        items={substringSearchStrategy(
                            entityStore.sublists,
                            searchQuery,
                            null,
                            (item, q) =>
                                item.listKey.toLocaleLowerCase().indexOf(q) !== -1 ||
                                item.synonyms.some(list => list.values.some(v => v.toLocaleLowerCase().indexOf(q) !== -1))
                        )}
                        sublistBeingEdited={
                            isPrebuilt(entityCapabilityBeingEdited) || isRegex(entityCapabilityBeingEdited)
                                ? null
                                : entityCapabilityBeingEdited
                        }
                    />
                </Stack>
            </CluEntityCapabilityCollapsableCard>

            <DeletePromptModal
                onDelete={onDeleteCapability}
                hidden={isDeletionModalHidden}
                disabled={defaultOperationsTracker.isRunning}
                onDismiss={() => setIsDeletionModalHidden(true)}
                dialogContentProps={{ title: localize(messages.deletionModalTitle) }}>
                <LuisText>{localize(messages.deletionModalBody)}</LuisText>
            </DeletePromptModal>
        </Stack>
    );
});
